export type CrudEntityKey = 'customers' | 'orders' | 'inventory' | 'users' | 'products';
export type DashboardTileKey = CrudEntityKey | 'reports';

export interface CrudRecord {
  id: string;
  [key: string]: string;
}

export interface FieldOption {
  label: string;
  value: string;
  description?: string;
}

export interface FieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'select';
  placeholder: string;
  required?: boolean;
  options?: FieldOption[];
}

export interface CrudEntityConfig {
  key: CrudEntityKey;
  title: string;
  singular: string;
  description: string;
  accent: string;
  formFields: FieldDefinition[];
  seedData: CrudRecord[];
}

export interface DashboardTile {
  key: DashboardTileKey;
  title: string;
  description: string;
  accent: string;
  route: string;
  supportsCrud: boolean;
}

export const CRUD_ENTITY_CONFIGS: Record<CrudEntityKey, CrudEntityConfig> = {
  customers: {
    key: 'customers',
    title: 'Customers',
    singular: 'Customer',
    description: 'Manage customer records and contact details.',
    accent: '#174c6b',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', placeholder: 'Enter customer name', required: true },
      { key: 'email', label: 'Email', type: 'email', placeholder: 'Enter customer email', required: true },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: 'Enter phone number', required: true },
      { key: 'city', label: 'City', type: 'text', placeholder: 'Enter city', required: true }
    ],
    seedData: [
      { id: 'C001', name: 'Amelia Stone', email: 'amelia.stone@example.com', phone: '555-0101', city: 'Seattle' },
      { id: 'C002', name: 'Jordan Mills', email: 'jordan.mills@example.com', phone: '555-0102', city: 'Austin' },
      { id: 'C003', name: 'Noah Harris', email: 'noah.harris@example.com', phone: '555-0103', city: 'Boston' }
    ]
  },
  orders: {
    key: 'orders',
    title: 'Orders',
    singular: 'Order',
    description: 'Track order status, value, and fulfillment.',
    accent: '#2d7d63',
    formFields: [
      { key: 'orderNumber', label: 'Order Number', type: 'text', placeholder: 'Enter order number', required: true },
      { key: 'customer', label: 'Customer', type: 'text', placeholder: 'Enter customer name', required: true },
      { key: 'status', label: 'Status', type: 'select', placeholder: 'Select status', required: true, options: [
        { label: 'Pending', value: 'Pending', description: 'Awaiting approval and payment confirmation.' },
        { label: 'Processing', value: 'Processing', description: 'Approved and currently being prepared.' },
        { label: 'Shipped', value: 'Shipped', description: 'Dispatched and on the way to the customer.' }
      ] },
      { key: 'amount', label: 'Amount', type: 'number', placeholder: 'Enter order amount', required: true }
    ],
    seedData: [
      { id: 'O001', orderNumber: 'ORD-1001', customer: 'Amelia Stone', status: 'Pending', amount: '1250' },
      { id: 'O002', orderNumber: 'ORD-1002', customer: 'Jordan Mills', status: 'Processing', amount: '870' },
      { id: 'O003', orderNumber: 'ORD-1003', customer: 'Noah Harris', status: 'Shipped', amount: '1940' }
    ]
  },
  inventory: {
    key: 'inventory',
    title: 'Inventory',
    singular: 'Inventory Item',
    description: 'Monitor stock levels and warehouse locations.',
    accent: '#8b5e34',
    formFields: [
      { key: 'itemName', label: 'Item Name', type: 'text', placeholder: 'Enter item name', required: true },
      { key: 'sku', label: 'SKU', type: 'text', placeholder: 'Enter SKU', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: 'Enter stock quantity', required: true },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Enter warehouse location', required: true }
    ],
    seedData: [
      { id: 'I001', itemName: 'Laptop Stand', sku: 'INV-201', quantity: '48', location: 'Aisle A1' },
      { id: 'I002', itemName: 'Wireless Mouse', sku: 'INV-202', quantity: '92', location: 'Aisle B3' },
      { id: 'I003', itemName: 'USB-C Dock', sku: 'INV-203', quantity: '34', location: 'Aisle C2' }
    ]
  },
  users: {
    key: 'users',
    title: 'Users',
    singular: 'User',
    description: 'Manage internal users and access roles.',
    accent: '#5d4aa8',
    formFields: [
      { key: 'fullName', label: 'Full Name', type: 'text', placeholder: 'Enter full name', required: true },
      { key: 'email', label: 'Email', type: 'email', placeholder: 'Enter email', required: true },
      { key: 'role', label: 'Role', type: 'select', placeholder: 'Select role', required: true, options: [
        { label: 'Admin', value: 'Admin' },
        { label: 'Manager', value: 'Manager' },
        { label: 'Viewer', value: 'Viewer' }
      ] },
      { key: 'status', label: 'Status', type: 'select', placeholder: 'Select status', required: true, options: [
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' }
      ] }
    ],
    seedData: [
      { id: 'U001', fullName: 'Leslie Morris', email: 'leslie.morris@example.com', role: 'Admin', status: 'Active' },
      { id: 'U002', fullName: 'Theo Grant', email: 'theo.grant@example.com', role: 'Manager', status: 'Active' },
      { id: 'U003', fullName: 'Diana Cole', email: 'diana.cole@example.com', role: 'Viewer', status: 'Inactive' }
    ]
  },
  products: {
    key: 'products',
    title: 'Products',
    singular: 'Product',
    description: 'Maintain the catalog used across orders and stock.',
    accent: '#c0563d',
    formFields: [
      { key: 'productName', label: 'Product Name', type: 'text', placeholder: 'Enter product name', required: true },
      { key: 'category', label: 'Category', type: 'text', placeholder: 'Enter category', required: true },
      { key: 'price', label: 'Price', type: 'number', placeholder: 'Enter product price', required: true },
      { key: 'availability', label: 'Availability', type: 'select', placeholder: 'Select availability', required: true, options: [
        { label: 'In Stock', value: 'In Stock' },
        { label: 'Low Stock', value: 'Low Stock' },
        { label: 'Backordered', value: 'Backordered' }
      ] }
    ],
    seedData: [
      { id: 'P001', productName: 'Office Desk', category: 'Furniture', price: '799', availability: 'In Stock' },
      { id: 'P002', productName: 'Ergonomic Chair', category: 'Furniture', price: '459', availability: 'Low Stock' },
      { id: 'P003', productName: 'Noise-Canceling Headset', category: 'Accessories', price: '219', availability: 'Backordered' }
    ]
  }
};

export const CRUD_ENTITY_ORDER: CrudEntityKey[] = [
  'customers',
  'orders',
  'inventory',
  'users',
  'products'
];

export const DASHBOARD_TILES: DashboardTile[] = [
  {
    key: 'customers',
    title: 'Customers',
    description: 'Contact details and customer master data.',
    accent: '#174c6b',
    route: '/records/customers',
    supportsCrud: true
  },
  {
    key: 'orders',
    title: 'Orders',
    description: 'Order queue with status and amount tracking.',
    accent: '#2d7d63',
    route: '/records/orders',
    supportsCrud: true
  },
  {
    key: 'inventory',
    title: 'Inventory',
    description: 'Warehouse stock and quantity visibility.',
    accent: '#8b5e34',
    route: '/records/inventory',
    supportsCrud: true
  },
  {
    key: 'reports',
    title: 'Reports',
    description: 'Read-only summary cards across the application.',
    accent: '#d07c38',
    route: '/reports',
    supportsCrud: false
  },
  {
    key: 'users',
    title: 'Users',
    description: 'Internal user accounts and their access roles.',
    accent: '#5d4aa8',
    route: '/records/users',
    supportsCrud: true
  },
  {
    key: 'products',
    title: 'Products',
    description: 'Catalog records used by orders and inventory.',
    accent: '#c0563d',
    route: '/records/products',
    supportsCrud: true
  }
];

export function isCrudEntityKey(value: string | null): value is CrudEntityKey {
  return value !== null && Object.hasOwn(CRUD_ENTITY_CONFIGS, value);
}